import React, { useEffect, useRef } from 'react';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import { Alert } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import api, { Company } from '../services/api';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'YOUR_API_KEY_HERE';

interface LocationPickerComponentProps {
  lat: number;
  lng: number;
  onLocationChange: (lat: number, lng: number) => void;
  primaryColor?: string;
  defaultZoom?: number;
  gestureHandling?: 'cooperative' | 'greedy' | 'none' | 'auto';
  height?: string;
  readOnly?: boolean;
}

const LocationPickerComponent: React.FC<LocationPickerComponentProps> = ({
  lat,
  lng,
  onLocationChange,
  primaryColor,
  defaultZoom,
  gestureHandling,
  height,
  readOnly,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [map, setMap] = React.useState<google.maps.Map>();
  const [marker, setMarker] = React.useState<google.maps.marker.AdvancedMarkerElement>();
  const [infoWindow, setInfoWindow] = React.useState<google.maps.InfoWindow>();

  useEffect(() => {
    if (ref.current && !map) {
      const initMap = async () => {
        const newMap = new window.google.maps.Map(ref.current!, {
          center: { lat, lng },
          zoom: defaultZoom ?? 3,
          gestureHandling: readOnly ? 'none' : (gestureHandling ?? 'auto'),
          disableDefaultUI: readOnly === true,
          clickableIcons: readOnly ? false : true,
          keyboardShortcuts: !readOnly,
          mapId: 'a7ecd8e0022096171640a52b', // Required for Advanced Markers
        });
        setMap(newMap);

        // Load marker library and create draggable marker
        const { AdvancedMarkerElement, PinElement } = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;

        // Create custom colored pin if primaryColor is provided
        let pinElement;
        if (primaryColor) {
          pinElement = new PinElement({
            background: primaryColor,
            borderColor: '#ffffff',
            glyphColor: '#ffffff',
            scale: 1.0,
          });
        }

        const newMarker = new AdvancedMarkerElement({
          position: { lat, lng },
          map: newMap,
          gmpDraggable: false,
          content: pinElement?.element,
        });
        setMarker(newMarker);

        if (!readOnly) {
          // Create and open InfoWindow
          const newInfoWindow = new google.maps.InfoWindow({
            content: '<h5>Zoom and <br/> Click on Map <br/> to move the marker</h5>',
          });
          newInfoWindow.open(newMap, newMarker as any);
          setInfoWindow(newInfoWindow);

          // Handle map clicks
          newMap.addListener('click', (e: google.maps.MapMouseEvent) => {
            const clickedLat = e.latLng?.lat() || 0;
            const clickedLng = e.latLng?.lng() || 0;
            newMarker.position = e.latLng!;
            onLocationChange(clickedLat, clickedLng);
          });
        }

      };

      initMap();
    }
  }, [ref, map, marker, lat, lng, onLocationChange, primaryColor, readOnly]);

  useEffect(() => {
    if (map && marker) {
      const newPosition = { lat, lng };
      map.setCenter(newPosition);
      marker.position = newPosition;

      // Keep InfoWindow open at new position
      if (infoWindow) {
        infoWindow.open(map, marker as any);
      }
    }
  }, [lat, lng, map, marker, infoWindow]);

  return <div ref={ref} style={{ width: '100%', height: height ?? '400px' }} />;
};

interface LocationPickerProps {
  lat: number;
  lng: number;
  onLocationChange: (lat: number, lng: number) => void;
  defaultZoom?: number;
  gestureHandling?: 'cooperative' | 'greedy' | 'none' | 'auto';
  height?: string;
  readOnly?: boolean;
}

const LocationPicker: React.FC<LocationPickerProps> = ({ lat, lng, onLocationChange, defaultZoom, gestureHandling, height, readOnly }) => {
  const [company, setCompany] = React.useState<Company | null>(null);
  const { userId } = useParams<{ userId: string }>();

  React.useEffect(() => {
    const loadCompany = async () => {
      try {
        const companyData = await api.getCompany(userId);
        setCompany(companyData);
      } catch (error) {
        console.error('Failed to load company data:', error);
      }
    };
    loadCompany();
  }, [userId]);

  const render = (status: Status) => {
    if (status === Status.LOADING) return <div>Loading Google Maps...</div>;
    if (status === Status.FAILURE) return <Alert variant="danger">Failed to load Google Maps</Alert>;
    return (
      <LocationPickerComponent
        lat={lat}
        lng={lng}
        onLocationChange={onLocationChange}
        primaryColor={company?.primaryColor}
        defaultZoom={defaultZoom}
        gestureHandling={gestureHandling}
        height={height}
        readOnly={readOnly}
      />
    );
  };

  return <Wrapper apiKey={GOOGLE_MAPS_API_KEY} render={render} />;
};

export default LocationPicker;